<template>
  <div>
    <create-comment
      :focus-add-comment-on-load="focusAddCommentOnLoad"
      :mentionable-users="usersInScope"
      ref="commentEditor"
      :org-id="goal.organizationId"
      :errors="commentErrors"
      :is-saving="isCreatingComment"
      :entity-type="ENTITY_TYPE.GOAL"
      :entity="goal"
      :show-subscribers="true"
      @save="postNewComment"
      @reset-errors="commentErrors = null"
    />
    <goal-events
      :event-to-expand="eventToFocus"
      v-model="events"
      :goal-id="goal.id"
      :mentionable-users="usersInScope"
      @reply="triggerReply"
      @awarded="refreshEvents"
      :selected-activity-filter="selectedActivityFilter"
    />
  </div>
</template>

<script>
/**
 * This component shows Goal Activities - comments/check-ins
 */
import GoalEvents from '@/components/GoalEvents'
import CreateComment from '@/components/CreateComment'
import { MIME_TYPE } from '@/constants/common'
import events from '@/constants/events'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import focusEventNode from '@/utils/focusEventNode'
import pickError from '@/utils/pickError'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import replyAsComment from '@/utils/replyAsComment'
import { trackEvent } from '../analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import * as R from 'ramda'

export default {
  name: 'GoalActivitySection',
  props: ['goal', 'focusAddCommentOnLoad', 'eventToFocus', 'selectedActivityFilter'],
  mixins: [MembershipsMixin, EventBusMixin],
  components: {
    GoalEvents,
    CreateComment
  },
  data() {
    return {
      events: [],
      commentErrors: null,
      isCreatingComment: false,
      ENTITY_TYPE
    }
  },
  computed: {
    usersInScope() {
      return this.getUsersInScope(this.goal.teamId, this.goal.organizationId)
    },
    team() {
      return this.$store.getters.teamById(this.goal.teamId)
    }
  },
  methods: {
    async postNewComment(comment) {
      try {
        this.isCreatingComment = true
        let event = await this.$store.dispatch('createEvent', {
          orgId: this.goal.organizationId,
          goalId: this.goal.id,
          event: {
            comment,
            commentType: MIME_TYPE.HTML
          }
        })
        this.events.unshift(event)
        EventBus.$emit('ADD_TOAST', { message: 'Comment has been posted successfully' })
        this.$refs.commentEditor.$refs.editor.setData(null)
        this.commentErrors = null
      } catch (e) {
        this.commentErrors = R.pathOr(pickError(e), ['response', 'data', 'detail', 'comment'], e)
      }
      this.isCreatingComment = false
      trackEvent('Activity', 'New Comment', 'Goal')
    },
    addEvent(event) {
      if (event.goalId === this.goal.id) this.events.unshift(event)
    },
    onEventsReloadEvent() {
      this.refreshEvents()
    },
    async refreshEvents() {
      this.events = await this.$store.dispatch('getGoalEvents', {
        orgId: this.goal.organizationId,
        goalId: this.goal.id,
        isArchived: this.goal.isArchived
      })
    },
    triggerReply(event) {
      replyAsComment(event, this.$store, this.$refs.commentEditor)
    }
  },
  async mounted() {
    this.listenToEventBus(events.CHECKIN_POSTED, this.addEvent)
    EventBus.$on('RELOAD_ACTIVITY_EVENTS', this.onEventsReloadEvent)
    await this.refreshEvents()
    if (this.goal.type === 'team' && this.team && this.team.isPrivate) {
      this.updateTeamMemberships(this.goal.organizationId, this.goal.teamId)
    } else {
      this.updateOrgMemberships(this.goal.organizationId)
    }
    this.$nextTick(() => {
      this.eventToFocus && focusEventNode(this.eventToFocus)
    })
  },
  beforeDestroy() {
    EventBus.$off('RELOAD_ACTIVITY_EVENTS', this.onEventsReloadEvent)
  },
  watch: {
    async eventToFocus(eventToFocus) {
      if (eventToFocus) {
        const event = this.events.find(x => x.id === +eventToFocus)
        if (!event) {
          await this.refreshEvents()
        }
        this.$nextTick(() => focusEventNode(eventToFocus))
      }
    }
  }
}
</script>

<style scoped></style>
